import type {
  AltYApartment,
  AltYBmadBuildState,
  AltYBmadWorkflow,
  AltYBootstrapApiConfig,
  AltYBootstrapAuthConfig,
  AltYBridgeContext,
  AltYChatGroup,
  AltYChatSummary,
  AltYIdea,
  AltYImageAttachment,
  AltYMintResponse,
  AltYMode,
  AltYParticipant,
  AltYUser,
} from '../types/global';
import { fetchWithAltYNativeAuth } from './nativeAuth';
import type { PinnedAgent } from './pinnedAgents';

// All Alt-Y data + auth is served by the broker. Embedded OneSheet uses the
// same-origin default; standalone/native shells may override it through the
// bootstrap adapter.
function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

function cleanUrl(value?: string | null): string | undefined {
  const trimmed = typeof value === 'string' ? trimTrailingSlashes(value.trim()) : '';
  return trimmed || undefined;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getAltYApiConfig(): Partial<AltYBootstrapApiConfig> {
  return window.altYResolvedBootstrap?.api
    || window.altYBootstrapConfig?.api
    || window.altYStandaloneConfig?.api
    || {};
}

function getAltYAuthConfig(): Partial<AltYBootstrapAuthConfig> {
  return window.altYResolvedBootstrap?.auth
    || window.altYBootstrapConfig?.auth
    || window.altYStandaloneConfig?.auth
    || {};
}

export function getAltYApiBase(): string {
  const configured = getAltYApiConfig().baseUrl
    || window.altYBootstrapConfig?.api?.baseUrl
    || window.altYStandaloneConfig?.api?.baseUrl
    || '/api/alty';
  return cleanUrl(configured) || '/api/alty';
}

function getAltYMintUrl(): string {
  return cleanUrl(getAltYApiConfig().mintUrl) || `${getAltYApiBase()}/mint`;
}

function wsProtocolForHttp(protocol: string): 'ws:' | 'wss:' | '' {
  if (protocol === 'https:') return 'wss:';
  if (protocol === 'http:') return 'ws:';
  if (protocol === 'wss:' || protocol === 'ws:') return protocol;
  return '';
}

function parseWebSocketUrl(value?: string | null): URL | null {
  const raw = cleanUrl(value);
  if (!raw) return null;
  try {
    const parsed = new URL(raw, window.location.href);
    const wsProtocol = wsProtocolForHttp(parsed.protocol);
    if (!wsProtocol || !parsed.hostname) return null;
    parsed.protocol = wsProtocol;
    parsed.hash = '';
    return parsed;
  } catch {
    return null;
  }
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '::1'
    || hostname === '[::1]';
}

function configuredAltYWsBase(): string | undefined {
  return cleanUrl(getAltYApiConfig().wsBaseUrl);
}

export function getAltYWsBase(): string {
  const configured = configuredAltYWsBase();
  if (configured) return configured;

  const apiBase = getAltYApiBase();
  try {
    const parsed = new URL(apiBase, window.location.href);
    const wsProtocol = wsProtocolForHttp(parsed.protocol);
    if (!wsProtocol) return '/ws';
    const apiPath = parsed.pathname.replace(/\/api\/alty\/?$/, '').replace(/\/+$/, '');
    parsed.protocol = wsProtocol;
    parsed.pathname = `${apiPath}/ws`;
    parsed.search = '';
    parsed.hash = '';
    return trimTrailingSlashes(parsed.href);
  } catch {
    return '/ws';
  }
}

function getAltYAuthUrl(
  apiKey: 'authMeUrl' | 'loginRequestUrl' | 'loginVerifyUrl' | 'logoutUrl',
  path: string,
): string {
  const api = getAltYApiConfig();
  const auth = getAltYAuthConfig();
  const explicitUrl = cleanUrl(api[apiKey])
    || (apiKey === 'loginRequestUrl' ? cleanUrl(auth.loginRequestUrl) : undefined)
    || (apiKey === 'loginVerifyUrl' ? cleanUrl(auth.loginVerifyUrl) : undefined)
    || (apiKey === 'logoutUrl' ? cleanUrl(auth.logoutUrl) : undefined);
  return explicitUrl || `${getAltYApiBase()}${path}`;
}

function originFromUrl(value?: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(value, window.location.href).origin;
  } catch {
    return null;
  }
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = summarizeErrorBody(await response.text());
    throw new Error(`HTTP ${response.status}${body ? ` ${body}` : ''}`);
  }
  return response.json() as Promise<T>;
}

function summarizeErrorBody(body: string): string {
  if (!body) return '';
  if (/502 Bad Gateway/i.test(body)) return 'Bad Gateway';
  if (/503 Service Unavailable/i.test(body)) return 'Service Unavailable';
  if (/504 Gateway Timeout/i.test(body)) return 'Gateway Timeout';
  return body
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

type RawAltYMintResponse = Omit<AltYMintResponse, 'oneSheetSessionId' | 'wsUrl'> & {
  oneSheetSessionId?: string;
  wsUrl?: string;
};

function wsBasePathForSocket(pathname: string): string {
  return pathname
    .replace(/\/+$/, '')
    .replace(/\/[^/]*$/, '')
    || '/';
}

function shouldPreferConfiguredWsBase(explicitWsUrl: string | undefined, configuredWsBase: string | undefined): boolean {
  const configured = parseWebSocketUrl(configuredWsBase);
  if (!configured || isLocalHostname(configured.hostname)) return false;
  if (!explicitWsUrl) return true;

  const explicit = parseWebSocketUrl(explicitWsUrl);
  if (!explicit) return true;
  if (explicit.protocol !== configured.protocol || explicit.host !== configured.host) return true;
  return wsBasePathForSocket(explicit.pathname) !== configured.pathname.replace(/\/+$/, '');
}

function wsUrlFromBase(
  wsBase: string,
  sessionId: string,
  bearer: string | undefined,
  fallbackSearch: string,
): string | null {
  const parsed = parseWebSocketUrl(wsBase);
  if (!parsed || !sessionId) return null;
  parsed.pathname = `${parsed.pathname.replace(/\/+$/, '')}/${encodeURIComponent(sessionId)}`;
  parsed.search = fallbackSearch;
  if (bearer) parsed.searchParams.set('bearer', bearer);
  return parsed.href;
}

function resolveMintWsUrl(raw: RawAltYMintResponse): string {
  const explicitWsUrl = cleanUrl(raw.wsUrl);
  const configuredWsBase = configuredAltYWsBase();
  const explicitParsed = parseWebSocketUrl(explicitWsUrl);
  const explicitSessionId = explicitParsed?.pathname.split('/').filter(Boolean).at(-1);
  const sessionId = stringOrUndefined(raw.oneSheetSessionId)
    || stringOrUndefined(raw.sessionId)
    || explicitSessionId
    || '';
  const bearer = stringOrUndefined(raw.bearer);
  const explicitSearch = explicitParsed?.search || '';

  if (shouldPreferConfiguredWsBase(explicitWsUrl, configuredWsBase)) {
    const configuredWsUrl = wsUrlFromBase(configuredWsBase || '', sessionId, bearer, explicitSearch);
    if (configuredWsUrl) return configuredWsUrl;
  }

  if (explicitParsed) return explicitParsed.href;
  if (explicitWsUrl) return explicitWsUrl;

  return wsUrlFromBase(getAltYWsBase(), sessionId, bearer, '') || getAltYWsBase();
}

function normalizeMintResponse(raw: RawAltYMintResponse): AltYMintResponse {
  const explicitSessionId = parseWebSocketUrl(raw.wsUrl)?.pathname.split('/').filter(Boolean).at(-1);
  const sessionId = stringOrUndefined(raw.sessionId)
    || stringOrUndefined(raw.oneSheetSessionId)
    || explicitSessionId
    || '';
  return {
    ...raw,
    sessionId,
    oneSheetSessionId: stringOrUndefined(raw.oneSheetSessionId) || sessionId,
    wsUrl: resolveMintWsUrl(raw),
  };
}

// --- auth -----------------------------------------------------------------

export async function getAuthedEmail(): Promise<string | null> {
  const response = await fetchWithAltYNativeAuth(getAltYAuthUrl('authMeUrl', '/auth/me'), { credentials: 'include' });
  if (!response.ok) return null;
  const data = (await response.json()) as { email?: string };
  return data.email || null;
}

export async function requestLoginCode(email: string): Promise<void> {
  const response = await fetchWithAltYNativeAuth(getAltYAuthUrl('loginRequestUrl', '/auth/request-code'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  await readJson(response);
}

export async function verifyLoginCode(email: string, code: string): Promise<void> {
  const response = await fetchWithAltYNativeAuth(getAltYAuthUrl('loginVerifyUrl', '/auth/verify-code'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  await readJson(response);
}

export async function logout(): Promise<void> {
  await fetchWithAltYNativeAuth(getAltYAuthUrl('logoutUrl', '/auth/logout'), { method: 'POST', credentials: 'include' }).catch(() => undefined);
}

// --- AUTO router ---------------------------------------------------------

// Hands prompt + candidate agents to the broker, which shells out to Haiku
// for a one-shot routing decision. Returns null on any failure so the caller
// can fall back to its in-process keyword heuristic.
export async function routeAuto(
  prompt: string,
  candidates: Array<{ id: string; label: string; description: string }>,
): Promise<{ chosenId: string; reason: string } | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/route-auto`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, candidates }),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { chosenId?: string; reason?: string };
    if (!data.chosenId) return null;
    return { chosenId: data.chosenId, reason: data.reason || '' };
  } catch {
    return null;
  }
}

// --- chats ----------------------------------------------------------------

// Sidebar now shows every mode's chats interleaved; calling with no `mode`
// hits the broker's unified path. Pass an explicit mode to scope the query
// (kept for callers that want a single mode's list).
export async function listAltYChats(mode?: AltYMode): Promise<AltYChatSummary[]> {
  const url = mode
    ? `${getAltYApiBase()}/chats?mode=${encodeURIComponent(mode)}`
    : `${getAltYApiBase()}/chats`;
  const response = await fetchWithAltYNativeAuth(url, { credentials: 'include' });
  return readJson<AltYChatSummary[]>(response);
}

// Intelligent sidebar search. Hands the query + the slim chat list (runId +
// 5-word title + ~20-word summary) to the broker, which tries Composer →
// Grok → Codex → Gemini → Claude until one returns ranked matches. Returns the ordered
// runIds, or null on any failure so the caller can fall back to a local
// substring filter (also the state before the broker restart that ships the
// /search-chats route).
export async function searchAltYChats(
  query: string,
  chats: Array<{ runId: number; title: string; subtitle: string; lastTurnSummary?: string }>,
): Promise<number[] | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/search-chats`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, chats }),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { matches?: unknown };
    if (!Array.isArray(data.matches)) return null;
    return data.matches.filter((m): m is number => typeof m === 'number');
  } catch {
    return null;
  }
}

export async function mintAltYSession(
  context: AltYBridgeContext | null,
  attachToRunId?: number | null,
  mode: AltYMode = 'interact',
  parentRunId?: number | null,
  targetBaseUrl?: string | null,
): Promise<AltYMintResponse> {
  const selectedRows = Array.isArray(context?.selectedRows) ? context.selectedRows.join(',') : context?.selectedRows || '';
  const selectedDocumentRows = Array.isArray(context?.selectedDocumentRows) && context.selectedDocumentRows.length
    ? context.selectedDocumentRows
    : null;
  const baseUrl = targetBaseUrl || context?.baseUrl || context?.origin || originFromUrl(context?.url);
  const payload = {
    pageUrl: context?.url || window.location.href,
    baseUrl,
    documentId: context?.documentId || null,
    documentTypeId: context?.documentTypeId || null,
    visibleFields: context?.visibleFields || {},
    selectedRows,
    selectedDocumentRows,
    attachToRunId: attachToRunId || null,
    parentRunId: parentRunId || null,
    mode,
    // The LoginAs customer the iframe is currently inside. The broker forwards
    // this to OneSheet's RegisterAltYBearer so the AgentSessionState gets the
    // right CustomerId — otherwise AgentProxy rejects with "session has no
    // customer" because the SuperAdmin's own User row has no CustomerId.
    customerId: context?.customerId || null,
    customerName: context?.customerName || null,
  };

  const response = await fetchWithAltYNativeAuth(getAltYMintUrl(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeMintResponse(await readJson<RawAltYMintResponse>(response));
}

export type AltYTranscriptResponse = {
  events: unknown[];
  lineCount?: number;
  fromLine?: number;
};

export async function getAltYTranscript(
  runId: number,
  afterLine?: number,
  lastTurns?: number,
  stripTools?: boolean,
  lastResponse?: boolean,
): Promise<AltYTranscriptResponse> {
  const params = new URLSearchParams();
  if (typeof afterLine === 'number' && Number.isFinite(afterLine) && afterLine > 0) {
    params.set('afterLine', String(Math.floor(afterLine)));
  }
  if (typeof lastTurns === 'number' && Number.isFinite(lastTurns) && lastTurns > 0) {
    params.set('lastTurns', String(Math.floor(lastTurns)));
  }
  if (stripTools) params.set('stripTools', '1');
  if (lastResponse) params.set('lastResponse', '1');
  const suffix = params.toString() ? `?${params}` : '';
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/transcript${suffix}`, { credentials: 'include' });
  return readJson<AltYTranscriptResponse>(response);
}

export async function getAltYToolOutput(runId: number, useId: string): Promise<{ content: unknown; isError: boolean }> {
  const response = await fetchWithAltYNativeAuth(
    `${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/tool/${encodeURIComponent(useId)}`,
    { credentials: 'include' },
  );
  return readJson<{ content: unknown; isError: boolean }>(response);
}

export function setAltYChatRunning(runId: number, running: boolean): Promise<void> {
  return fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/running`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ running }),
  }).then(() => undefined, () => undefined);
}

// `auto` marks a non-binding placeholder rename (the first-message title) that
// the broker's post-turn auto-namer is still allowed to overwrite. A real
// manual rename omits it, which locks the chat against auto-naming server-side.
export async function renameAltYChat(runId: number, title: string, auto = false): Promise<void> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/rename`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, auto }),
  });
  await readJson(response);
}

export async function deleteAltYChat(runId: number): Promise<void> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  await readJson(response);
}

// --- chat groups (sidebar folders) ---------------------------------------
// Owner-scoped folders the panel surfaces above the chat list. Same general
// shape as the agents store: list / create / update / delete; assignment goes
// through POST /chats/<runId>/group (passing groupId:null clears it).

export async function listAltYChatGroups(): Promise<AltYChatGroup[]> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chat-groups`, { credentials: 'include' });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? (data as AltYChatGroup[]) : [];
  } catch {
    return [];
  }
}

export async function createAltYChatGroup(label: string, color = ''): Promise<AltYChatGroup | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chat-groups`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, color }),
    });
    if (!response.ok) return null;
    return (await response.json()) as AltYChatGroup;
  } catch {
    return null;
  }
}

export async function updateAltYChatGroup(
  groupId: number,
  patch: Partial<Pick<AltYChatGroup, 'label' | 'color' | 'position'>>,
): Promise<AltYChatGroup | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chat-groups/${encodeURIComponent(groupId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) return null;
    return (await response.json()) as AltYChatGroup;
  } catch {
    return null;
  }
}

export async function deleteAltYChatGroup(groupId: number): Promise<boolean> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chat-groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function setAltYChatGroup(runId: number, groupId: number | null): Promise<boolean> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${encodeURIComponent(runId)}/group`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function startBmadBuild(runId: number, workflow: AltYBmadWorkflow): Promise<AltYBmadBuildState> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/bmad-build`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId, workflow }),
  });
  return readJson<AltYBmadBuildState>(response);
}

export async function getBmadBuild(runId: number): Promise<AltYBmadBuildState | null> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/bmad-build/${encodeURIComponent(runId)}`, {
    credentials: 'include',
  });
  if (response.status === 404) return null;
  return readJson<AltYBmadBuildState>(response);
}

// --- user-pinned agents (owner-scoped, server-side) ----------------------
// Replaces the old localStorage.altY.userAgents.v1 store so a pin minted on
// one browser/device shows up everywhere the same user signs in.

export async function listAltYAgents(): Promise<PinnedAgent[]> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/agents`, { credentials: 'include' });
  return readJson<PinnedAgent[]>(response);
}

export async function createAltYAgent(agent: PinnedAgent): Promise<PinnedAgent> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/agents`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(agent),
  });
  return readJson<PinnedAgent>(response);
}

export async function updateAltYAgent(id: string, patch: Partial<PinnedAgent>): Promise<PinnedAgent> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/agents/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return readJson<PinnedAgent>(response);
}

export async function deleteAltYAgent(id: string): Promise<void> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/agents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readJson(response);
}

// --- Idea Agent list (owner + agent + department scoped) -----------------
// Backs the Idea Agent pin's idea list. The panel captures the user's message
// as an idea (addIdea), shows the saved list (listIdeas), and lets the user
// delete (deleteIdea). All calls degrade to empty/no-op on failure so the UI
// stays usable before the broker restart that ships these very endpoints.

export async function listIdeas(agentId: string, department: string): Promise<AltYIdea[]> {
  try {
    const params = new URLSearchParams({ agentId, department: department || 'default' });
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/ideas?${params.toString()}`, { credentials: 'include' });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? (data as AltYIdea[]) : [];
  } catch {
    return [];
  }
}

// Returns { added, idea }: `added` is false when an identical idea already
// exists in this scope (the broker dedupes), in which case `idea` is the
// pre-existing match — so the UI can tell the user it was already on the list.
export async function addIdea(
  agentId: string,
  department: string,
  text: string,
): Promise<{ added: boolean; idea: AltYIdea } | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/ideas`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId, department: department || 'default', text }),
    });
    if (!response.ok) return null;
    return (await response.json()) as { added: boolean; idea: AltYIdea };
  } catch {
    return null;
  }
}

export async function deleteIdea(ideaId: string): Promise<boolean> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/ideas/${encodeURIComponent(ideaId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function reorderIdeas(
  agentId: string,
  department: string,
  order: string[],
): Promise<AltYIdea[]> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/ideas/reorder`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId, department: department || 'default', order }),
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? (data as AltYIdea[]) : [];
  } catch {
    return [];
  }
}

// --- Apartment list (owner + agent + department scoped) ------------------
// Backs the "Recent Apartment Origin Finder" pin's saved cards. The agent emits
// found listings as a fenced JSON block; the panel parses it and POSTs each
// apartment (addApartment), shows the saved cards newest-first (listApartments),
// and lets the user delete one (deleteApartment). All calls degrade to
// empty/no-op on failure so the UI stays usable before the broker restart that
// ships these very endpoints.

export async function listApartments(agentId: string, department: string): Promise<AltYApartment[]> {
  try {
    const params = new URLSearchParams({ agentId, department: department || 'default' });
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/apartments?${params.toString()}`, { credentials: 'include' });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? (data as AltYApartment[]) : [];
  } catch {
    return [];
  }
}

// Returns { added, apartment }: `added` is false when an apartment with the same
// normalized source/origin URL already exists in this scope (the broker dedupes),
// in which case `apartment` is the pre-existing match.
export async function addApartment(
  agentId: string,
  department: string,
  apartment: AltYApartment,
): Promise<{ added: boolean; apartment: AltYApartment } | null> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/apartments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId, department: department || 'default', apartment }),
    });
    if (!response.ok) return null;
    return (await response.json()) as { added: boolean; apartment: AltYApartment };
  } catch {
    return null;
  }
}

export async function deleteApartment(aptId: string): Promise<boolean> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/apartments/${encodeURIComponent(aptId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

// --- per-user settings (owner-scoped, server-side) -----------------------
// A small per-superadmin settings blob the broker stores in its chat DB, so
// things like the ElevenLabs Scribe key/model follow the user across browsers
// instead of living only in one browser's localStorage. Both calls degrade to
// {} on any failure so the caller can fall back to its localStorage cache.

export type AltYUserSettings = {
  scribeApiKey?: string;
  // Groq (Bearer) key, used by the groq_whisper_* speech-to-text models.
  scribeGroqApiKey?: string;
  scribeModel?: 'scribe_v2_realtime' | 'scribe_v2' | 'groq_whisper_v3' | 'groq_whisper_v3_turbo';
  // Spoken-language hint for transcription, or 'auto' to let the provider detect.
  scribeLanguage?: string;
  // Once true, the default-on auto-submit nudge should not be shown again for this owner.
  autoSubmitTipSeen?: boolean;
  // BYOK keys for the openrouter / openai-chat MCP tools agents can call from
  // any chat. Stored server-side per SuperAdmin owner and re-emitted into each
  // workspace's .mcp.json on every turn so a key change applies immediately.
  openrouterApiKey?: string;
  openaiApiKey?: string;
  // Echoed back by GET so the UI can show which SuperAdmin owner holds the key.
  owner?: string;
};

export async function getAltYSettings(): Promise<AltYUserSettings> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/settings`, { credentials: 'include' });
    if (!response.ok) return {};
    return (await response.json()) as AltYUserSettings;
  } catch {
    return {};
  }
}

export async function updateAltYSettings(patch: AltYUserSettings): Promise<AltYUserSettings> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) return {};
    return (await response.json()) as AltYUserSettings;
  } catch {
    return {};
  }
}

// --- broker restart -------------------------------------------------------
// The broker runs old in-memory code until restarted (uvicorn has no reload).
// `restartNeeded` is true when an Alt-Y dev turn edited the broker source after
// the running process loaded it — the panel flags this (amber ⚙ + button) and
// the "Restart Alt-Y server" button POSTs the restart. The broker execs uvicorn
// in-place, so detached provider/dev workers keep running and existing active
// chats are not killed. Both degrade safely if the routes aren't live yet (i.e.
// before the first restart that ships these very endpoints).

export type BrokerStatus = { restartNeeded: boolean; reason: string };

export async function getBrokerStatus(): Promise<BrokerStatus> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/broker-status`, { credentials: 'include' });
    if (!response.ok) return { restartNeeded: false, reason: '' };
    const data = (await response.json()) as Partial<BrokerStatus>;
    return { restartNeeded: !!data.restartNeeded, reason: data.reason || '' };
  } catch {
    return { restartNeeded: false, reason: '' };
  }
}

export async function waitForBrokerReady(timeoutMs = 30000, intervalMs = 1000): Promise<BrokerStatus | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() <= deadline) {
    try {
      const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/broker-status?t=${Date.now()}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (response.ok) {
        const data = (await response.json()) as Partial<BrokerStatus>;
        return { restartNeeded: !!data.restartNeeded, reason: data.reason || '' };
      }
    } catch {
      /* broker is still coming back */
    }
    await sleep(intervalMs);
  }
  return null;
}

export async function restartBroker(): Promise<boolean> {
  try {
    const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/broker-restart`, { method: 'POST', credentials: 'include' });
    return response.ok;
  } catch {
    // The connection often drops as the broker exits — treat that as success.
    return true;
  }
}

// --- app bundle version ---------------------------------------------------
// The running bundle bakes in `__ALTY_BUILD_ID__` at build time; the deployed
// bundle's id lives in dist/version.json. When they differ, a newer Alt-Y app
// has been deployed and the panel offers a reload.
//
// The id is fetched from whichever of these the current host can reach,
// in priority order:
//   1. /api/alty/app-version — proxied to the broker on ai; absent on
//      testing/internal/online (their nginx doesn't proxy /api/alty/*).
//   2. /alty-app/version.json — the static file shipped inside the deployed
//      bundle, served same-origin by every embedded host (and ai). On
//      testing/internal/online this is the only signal we have.
//   3. /version.json — same file at the broker's standalone root.
// Cache-busted + no-store so we always see the freshest id. Returns null
// (treated as "no update") if none of the paths answer.

async function fetchBuildId(url: string): Promise<string | null> {
  try {
    const response = await fetchWithAltYNativeAuth(url, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) return null;
    const data = (await response.json()) as { buildId?: string };
    return typeof data.buildId === 'string' && data.buildId ? data.buildId : null;
  } catch {
    return null;
  }
}

export async function getDeployedAppBuildId(): Promise<string | null> {
  const stamp = Date.now();
  const resolvedStandalone = window.altYResolvedBootstrap?.shell?.standalone;
  const standalone = typeof resolvedStandalone === 'boolean'
    ? resolvedStandalone
    : window.altYShellConfig?.standalone === true;
  const candidates = [
    `${getAltYApiBase()}/app-version?t=${stamp}`,
    standalone ? `/version.json?t=${stamp}` : `/alty-app/version.json?t=${stamp}`,
  ];
  for (const url of candidates) {
    const id = await fetchBuildId(url);
    if (id) return id;
  }
  return null;
}

// --- Online Dev MVP -------------------------------------------------------

export type ShipOnlineDevResult = {
  ok: boolean;
  committed?: boolean;
  devName?: string;
  devUrl?: string;
  error?: string;
};

// Ship It button: commits + pushes the chat's online21 dev fork to the
// upstream `testing` branch. Returns either `ok:true` on success or a 4xx/5xx
// with `{ok:false, error}` body; we surface either to the user.
export async function shipOnlineDev(runId: number): Promise<ShipOnlineDevResult> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/online-dev/ship`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId }),
  });
  let body: ShipOnlineDevResult = { ok: false };
  try {
    body = (await response.json()) as ShipOnlineDevResult;
  } catch {
    body = { ok: false, error: `HTTP ${response.status}` };
  }
  if (!response.ok && !body.error) body.error = `HTTP ${response.status}`;
  return body;
}

// --- Users & Chat Participants -------------------------------------------

export async function listAltYUsers(): Promise<AltYUser[]> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/users`, { credentials: 'include' });
  return readJson<AltYUser[]>(response);
}

export async function listAltYParticipants(chatId: number): Promise<AltYParticipant[]> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${chatId}/participants`, { credentials: 'include' });
  return readJson<AltYParticipant[]>(response);
}

export async function addAltYParticipant(
  chatId: number,
  params: {
    type: 'user' | 'agent';
    id: string;
    label?: string;
    agentId?: string;
    mode?: string;
  }
): Promise<{ participant: AltYParticipant; participants: AltYParticipant[] }> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${chatId}/participants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    credentials: 'include',
  });
  return readJson<{ participant: AltYParticipant; participants: AltYParticipant[] }>(response);
}

export async function addAltYHumanMessage(
  chatId: number,
  params: {
    text: string;
    recipientId?: string;
    recipientLabel?: string;
    images?: AltYImageAttachment[];
  }
): Promise<{ event: any; participants: AltYParticipant[] }> {
  const response = await fetchWithAltYNativeAuth(`${getAltYApiBase()}/chats/${chatId}/human-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    credentials: 'include',
  });
  return readJson<{ event: any; participants: AltYParticipant[] }>(response);
}
